/**
 * Server-side Guardian conversation history (audit P-12, 2026-10-22).
 *
 * Loads the signed-in user's conversation list from the agent and exposes a
 * loader for a single conversation's messages. Shared-station-PC hygiene is the
 * hard rule: the list is scoped to the current userId, reset the instant the
 * user changes, and the previous login's device cache is wiped. When the agent
 * does not support the endpoint (older build: 404/503) or the network is down,
 * `supported` is false and the caller keeps its existing local behaviour without
 * any error surfaced.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchGuardianConversations, fetchGuardianConversation } from '../api/guardianHistoryApi';
import {
  loadCachedList,
  saveCachedList,
  clearGuardianHistoryCache,
} from '../utils/guardianHistoryCache';

/**
 * @param {{ userId: string|null, authToken: string|null, wsUrl?: string }} params
 */
export function useGuardianHistory({ userId, authToken, wsUrl }) {
  const [conversations, setConversations] = useState([]);
  const [supported, setSupported] = useState(true);
  const [loading, setLoading] = useState(false);
  // Guards against a stale response painting a previous login's list.
  const userRef = useRef(userId);
  userRef.current = userId;

  const reload = useCallback(async () => {
    if (!userId || !authToken) {
      setConversations([]);
      return;
    }
    // Paint from the per-user cache first (ids/titles only); never cross-user.
    setConversations(loadCachedList(userId));
    setLoading(true);
    const res = await fetchGuardianConversations(authToken, { wsUrl });
    // Drop the result if the user changed while the request was in flight.
    if (userRef.current !== userId) return;
    setLoading(false);
    setSupported(res.supported);
    if (res.supported) {
      setConversations(res.items);
      saveCachedList(userId, res.items);
    }
  }, [userId, authToken, wsUrl]);

  // Reset immediately on any user change so no previous login's list is shown.
  useEffect(() => {
    setConversations([]);
    setSupported(true);
    if (!userId || !authToken) return;
    reload();
  }, [userId, authToken, reload]);

  const loadConversation = useCallback(
    (id) => {
      if (!userId || !authToken) {
        return Promise.resolve({ supported: true, found: false, messages: [], conversationId: null, status: 0 });
      }
      return fetchGuardianConversation(authToken, id, { wsUrl });
    },
    [userId, authToken, wsUrl],
  );

  /** Wipes the current user's device cache and in-memory list (sign-out). */
  const clearCache = useCallback(() => {
    clearGuardianHistoryCache(userRef.current);
    setConversations([]);
  }, []);

  return { conversations, supported, loading, reload, loadConversation, clearCache };
}
